<template>
  <div class="guides-page mt-[30px] md:mt-0">
    <SecondaryHero
      :heading="$t('pages.guides.heroTitle')"
      :text="$t('pages.guides.heroText')"
      background-image="/hero.webp"
    />
    <div
      class="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5"
    >
      <GuideCard v-for="guide in guides" :key="guide.path" :guide="guide" />
    </div>
    <div v-if="isMoreGuidesToLoad" class="flex justify-center">
      <button
        type="button"
        :diabled="isLoading"
        class="mt-3 ml-7.5 rounded-sm border border-black px-3 py-1.5 text-xs font-bold transition-all duration-200 hover:bg-black hover:text-gold"
        :class="{
          'cursor-default bg-black text-gold opacity-50': isLoading,
          'cursor-pointer': !isLoading,
        }"
        :title="$t('general.showMore')"
        @click.prevent="loadMore"
      >
        {{ $t("general.showMore") }}
      </button>
    </div>
    <CitiesList />
  </div>
</template>

<script lang="ts" setup>
import type { Guide } from "@/types";
import { useGuidesApi } from "@/composables/useGuidesApi";

const { getGuides } = useGuidesApi();
const config = useRuntimeConfig();
const { t, locale, locales, defaultLocale } = useI18n();

const offset = ref(0);
const guidesPerPage = ref(20);
const isLoading = ref(false);
const isMoreGuidesToLoad = ref(true);

const key = computed(() => {
  return `${offset.value}:guides`;
});

const { data: guides } = useAsyncData<Guide[]>(
  key.value,
  async () => {
    return await getGuides({
      limit: guidesPerPage.value,
    });
  },
  {
    lazy: true,
    default: () => [],
  },
);

const loadMore = async () => {
  isLoading.value = true;
  offset.value += guidesPerPage.value;
  const response = await getGuides({
    limit: guidesPerPage.value,
    offset: offset.value,
  });

  guides.value.push(...response);
  if (response.length < guidesPerPage.value) {
    isMoreGuidesToLoad.value = false;
  }
  isLoading.value = false;
};

const metaTitle = computed(() => t("pages.guides.seo_title"));
const metaDescription = computed(() => t("pages.guides.seo_meta_description"));

useHead({
  title: metaTitle.value,
  meta: [{ name: "description", content: metaDescription.value }],
  link: generateSEOLinks(
    locales.value,
    defaultLocale,
    locale.value,
    config.public.websiteUrl,
    [
      {
        en: "guides",
        es: "guides",
        de: "guides",
        pl: "guides",
      },
    ],
  ),
});
</script>
